import { GradientOverlay } from '@components/GradientOverlay/GradientOverlay';
import { SelectableCard } from '@components/SelectableCard/SelectableCard';
import { Toast } from '@components/Toast/Toast';
import { isCompactCell } from '@components/VirtualMasonry/masonryConstants';
import { useMediaFilePreview } from '@lib/hooks/preview/preview';
import { getMediaThumbnail, useMediaThumbnail } from '@lib/image/thumbnail';
import { HLS_PLAYLIST_PATH } from '@lib/network/preview';
import { isAudioMedia } from '@lib/utils/fileExtensions';
import { getMediaUrl } from '@lib/utils/url';
import { LibraryFolderStore, useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { FileUploadStatus, useUploadManager } from '@stores/UploadManager/UploadManager';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMediaDeleteModal } from '../../context/MediaDeleteModalContext';
import {
  canOpenMediaModal,
  getDisplayProgress,
  getMediaAltText,
  getMediaDuration,
  isFailedOrphanUpload,
  isProbablyImage,
} from '../../utils/mediaStatus';
import { calculateUploadStats } from '../../utils/uploadCalculations';
import { StatusOverlay } from './components/StatusOverlay';
import { Thumbnail } from './components/Thumbnail';
import { VideoCard } from './components/VideoCard';
import css from './MediaCard.module.scss';

type MediaStatus = 'queued' | 'errored' | 'empty' | 'processing' | 'uploading' | 'completed';

interface StatusConfig {
  statusOverlay: React.ReactNode;
  previewContent: React.ReactNode;
}

interface MediaThumbnailProps {
  thumbnailUrl: string | null;
  alt: string | undefined;
  showIfNoThumbnail?: boolean;
  token?: string | null;
  localThumbnail?: string;
  gradientSeed?: string;
  isAudio?: boolean;
}

const MediaThumbnail = ({
  thumbnailUrl,
  alt,
  showIfNoThumbnail = false,
  token,
  localThumbnail,
  gradientSeed,
  isAudio,
}: MediaThumbnailProps) => {
  // Provide fallback alt text if none is provided
  const fallbackAlt = alt || 'Media thumbnail';

  if (!thumbnailUrl && !showIfNoThumbnail) {
    // Show LQIP based on alt text when no thumbnail and not showing placeholder
    return (
      <Thumbnail src={undefined} alt={fallbackAlt} gradientSeed={gradientSeed} isAudio={isAudio} />
    );
  }
  return (
    <Thumbnail
      src={thumbnailUrl || undefined}
      alt={fallbackAlt}
      token={token}
      localThumbnail={localThumbnail}
      gradientSeed={gradientSeed}
      isAudio={isAudio}
    />
  );
};

const getProcessingStatusConfig = (
  file: Media,
  thumbnailUrl: string | null,
  thumbnailToken: string | null,
  renderVideo: (options?: { showDuration?: boolean; showLowerBar?: boolean }) => React.ReactNode,
  folder: LibraryFolderStore,
  openDeleteModal: (media: Media) => void,
  canDelete?: boolean,
  isSelected?: boolean,
  displayProgress?: number,
  duration?: number,
  title?: string,
  localThumbnail?: string,
  isCompact?: boolean,
  isAudio?: boolean,
): StatusConfig => {
  const videoReady = file.capabilities?.preview;
  const onClose = canDelete ? () => openDeleteModal(file) : undefined;

  const createProcessingOverlay = (
    subtype: 'processing-with-video' | 'processing-without-video',
    progress?: number,
  ) => (
    <StatusOverlay
      type="processing"
      subtype={subtype}
      progress={progress ?? displayProgress}
      closeButtonProps={
        subtype === 'processing-without-video' && onClose
          ? { onClose, tooltip: 'Delete processing media' }
          : undefined
      }
      selectionCheckboxProps={
        isSelected
          ? {
              selection: folder.videoSelection,
              itemId: file._id,
            }
          : undefined
      }
      title={title ?? getMediaAltText(file)}
      media={file}
      duration={duration}
      isCompact={isCompact}
    />
  );

  if (videoReady) {
    return {
      statusOverlay: createProcessingOverlay('processing-with-video'),
      previewContent: renderVideo({ showLowerBar: false }),
    };
  } else if (thumbnailUrl) {
    return {
      statusOverlay: createProcessingOverlay('processing-without-video'),
      previewContent: (
        <MediaThumbnail
          thumbnailUrl={thumbnailUrl}
          alt={getMediaAltText(file)}
          gradientSeed={file.uniqueId || file._id}
          token={thumbnailToken}
          localThumbnail={localThumbnail}
          isAudio={isAudio}
        />
      ),
    };
  } else {
    return {
      statusOverlay: createProcessingOverlay('processing-without-video'),
      previewContent: (
        <MediaThumbnail
          thumbnailUrl={null}
          alt={getMediaAltText(file)}
          gradientSeed={file.uniqueId || file._id}
          showIfNoThumbnail={true}
          token={thumbnailToken}
          localThumbnail={localThumbnail}
          isAudio={isAudio}
        />
      ),
    };
  }
};

export const MediaCard = observer(function MediaCard({
  file,
  fileUploadStatus,
  isVisible,
  isHighlighted,
  cellWidth,
  ...divProps
}: {
  file: Media;
  fileUploadStatus?: FileUploadStatus;
  isVisible?: boolean;
  isHighlighted?: boolean;
  cellWidth?: number;
} & React.HTMLAttributes<HTMLDivElement>) {
  const loadStuff = isVisible;
  const [persistedThumbnailUrl, setPersistedThumbnailUrl] = useState<string | null>(null);
  const [persistedUploadStatus, setPersistedUploadStatus] = useState<FileUploadStatus | undefined>(
    undefined,
  );
  const [persistedLocalThumbnail, setPersistedLocalThumbnail] = useState<string | undefined>(
    () => fileUploadStatus?.localThumbnail,
  );
  const [persistedLocalDuration, setPersistedLocalDuration] = useState<number | undefined>(
    () => fileUploadStatus?.metadata?.duration,
  );
  const [persistedLocalName, setPersistedLocalName] = useState<string | undefined>(
    () => fileUploadStatus?.metadata?.name ?? fileUploadStatus?.file?.name,
  );
  const currentThumbnailData = useMediaThumbnail(
    loadStuff ? file.libraryId : undefined,
    loadStuff ? file._id : undefined,
  );
  const library = useLibraryManager().libraryById(file.libraryId);
  const libraryManager = useLibraryManager();
  const folder = useLibraryFolder();
  const uploadManager = useUploadManager();
  const canDelete = library && !library.isReadOnly;
  const { openDeleteModal } = useMediaDeleteModal();
  const navigate = useNavigate();
  const canOpenModal = canOpenMediaModal(file);

  // Only persist the backend thumbnail URL once the backend has actually generated it.
  // Without this gate, useMediaThumbnail constructs a URL that 404s during processing,
  // which would shadow the YouTube thumbnail from userMetadata.
  useEffect(() => {
    if (currentThumbnailData?.url && file.capabilities?.preview) {
      setPersistedThumbnailUrl(currentThumbnailData.url);
    }
  }, [currentThumbnailData?.url, file.capabilities?.preview]);

  // Persist upload status until backend takes over to bridge the gap
  // between upload completion and backend processing start
  useEffect(() => {
    if (fileUploadStatus) {
      // Update persisted status whenever we have a live one
      setPersistedUploadStatus(fileUploadStatus);
    } else if (file.status !== 'empty') {
      // File is no longer empty (backend started processing) - clear persisted status
      setPersistedUploadStatus(undefined);
    }
    // If fileUploadStatus is cleared and file.status is still 'empty',
    // keep showing persisted upload UI until backend transitions
  }, [fileUploadStatus, file.status]);

  // Capture the local thumbnail from the upload and keep it until the CDN thumbnail is ready.
  // persistedUploadStatus is cleared once file.status leaves 'empty', so we need
  // a separate value that survives through queued/processing states.
  useEffect(() => {
    const thumb = fileUploadStatus?.localThumbnail;
    if (thumb) setPersistedLocalThumbnail(thumb);
  }, [fileUploadStatus?.localThumbnail]);

  // Persist local duration and name from upload metadata until the backend provides its own.
  useEffect(() => {
    const dur = fileUploadStatus?.metadata?.duration;
    if (dur !== undefined) setPersistedLocalDuration(dur);
  }, [fileUploadStatus?.metadata?.duration]);

  useEffect(() => {
    const name = fileUploadStatus?.metadata?.name ?? fileUploadStatus?.file?.name;
    if (name) setPersistedLocalName(name);
  }, [fileUploadStatus?.metadata?.name, fileUploadStatus?.file?.name]);

  const { url: rawThumbnailUrl, token: thumbnailToken } = getMediaThumbnail(
    file,
    persistedThumbnailUrl || currentThumbnailData?.url,
    currentThumbnailData?.token,
  );
  const thumbnailUrl = rawThumbnailUrl || persistedLocalThumbnail || null;
  const { url: src, token: srcToken } = useMediaFilePreview(
    file.libraryId,
    file._id,
    HLS_PLAYLIST_PATH,
  );
  const status = file.status as MediaStatus;
  const progress = file.progress;

  const renderVideo = ({
    showLowerLeft = false,
    showLowerRight = false,
    showDuration,
    showLowerBar,
  }: {
    showLowerLeft?: boolean;
    showLowerRight?: boolean;
    showDuration?: boolean;
    showLowerBar?: boolean;
  } = {}) => {
    return thumbnailUrl ? (
      <VideoCard
        media={file}
        src={src || ''}
        libraryId={file.libraryId}
        mediaId={file._id}
        thumbnail={thumbnailUrl || undefined}
        thumbnailToken={thumbnailToken}
        isAudio={isAudio}
        cellWidth={cellWidth}
        options={{ showLowerLeft, showLowerRight, showDuration, showLowerBar }}
      />
    ) : (
      <MediaThumbnail
        thumbnailUrl={null}
        alt={getMediaAltText(file)}
        gradientSeed={file.uniqueId || file._id}
        showIfNoThumbnail={true}
        token={thumbnailToken}
        localThumbnail={persistedLocalThumbnail}
        isAudio={isAudio}
      />
    );
  };

  // Extract reusable onClose handler
  const createDeleteHandler = (file: Media) => () => openDeleteModal(file);

  const videoSelection = folder.videoSelection;
  const isSelected = videoSelection.isSelected(file._id);

  // Duration priority: backend → external source (e.g. YouTube) → local file metadata captured during upload
  // getMediaDuration treats backend duration=0 as "unknown" and falls through to the next source.
  // Still images (frameCount=1) don't have a meaningful duration to show.
  const duration = isProbablyImage(file)
    ? undefined
    : (getMediaDuration(file) ?? persistedLocalDuration);

  // Title priority: backend metadata → local file name captured during upload
  const localDisplayTitle = file.metadata?.name || persistedLocalName || `Media ${file._id}`;
  const isCompact = isCompactCell(cellWidth);

  const isAudio = isAudioMedia(file);

  const getStatusConfig = (): StatusConfig => {
    // Shared with the table view's StatusCell and used for the queued/processing pills.
    const displayProgress = getDisplayProgress(file);
    // When backend still shows "empty" but we have an active upload, show upload progress instead of empty placeholder
    // Use persisted upload status as fallback to bridge the gap when UploadManager clears the status
    const activeUploadStatus = fileUploadStatus || persistedUploadStatus;
    const isEmptyWithActiveUpload =
      file.status === 'empty' &&
      activeUploadStatus &&
      (activeUploadStatus.status === 'queued' ||
        activeUploadStatus.status === 'uploading' ||
        activeUploadStatus.status === 'success');
    if (isEmptyWithActiveUpload && activeUploadStatus) {
      // Show "Queued" when file is waiting in upload queue
      if (activeUploadStatus.status === 'queued') {
        return {
          statusOverlay: (
            <StatusOverlay
              type="queued-upload"
              title={localDisplayTitle}
              media={file}
              duration={duration}
              isCompact={isCompact}
              closeButtonProps={
                canDelete
                  ? {
                      onClose: createDeleteHandler(file),
                      tooltip: 'Clear queued upload',
                    }
                  : undefined
              }
              selectionCheckboxProps={
                isSelected ? { selection: videoSelection, itemId: file._id } : undefined
              }
            />
          ),
          previewContent: (
            <MediaThumbnail
              thumbnailUrl={thumbnailUrl}
              alt={getMediaAltText(file)}
              gradientSeed={file.uniqueId || file._id}
              localThumbnail={persistedLocalThumbnail}
              isAudio={isAudio}
            />
          ),
        };
      }

      // Show uploading progress for active uploads
      const uploadProgress =
        activeUploadStatus.status === 'success'
          ? 100
          : Math.max(0, Math.min(100, activeUploadStatus.progress || 0));
      const { subtext } = calculateUploadStats(activeUploadStatus, uploadProgress);
      return {
        statusOverlay: (
          <StatusOverlay
            type="uploading"
            progress={Math.floor(uploadProgress)}
            timeLeft=""
            title={localDisplayTitle}
            subtext={subtext}
            media={file}
            duration={duration}
            isCompact={isCompact}
            closeButtonProps={
              canDelete
                ? {
                    onClose: createDeleteHandler(file),
                    tooltip: 'Clear upload',
                  }
                : undefined
            }
            selectionCheckboxProps={
              isSelected ? { selection: videoSelection, itemId: file._id } : undefined
            }
          />
        ),
        previewContent: (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ),
      };
    }

    const configs: Record<MediaStatus, StatusConfig> = {
      queued: {
        statusOverlay: (
          <StatusOverlay
            type="queued-processing"
            progress={displayProgress}
            title={localDisplayTitle}
            media={file}
            duration={duration}
            isCompact={isCompact}
            closeButtonProps={{
              onClose: createDeleteHandler(file),
              tooltip: 'Delete queued media',
            }}
            selectionCheckboxProps={
              isSelected
                ? {
                    selection: videoSelection,
                    itemId: file._id,
                  }
                : undefined
            }
          />
        ),
        previewContent: (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ),
      },
      errored: {
        statusOverlay: (
          <StatusOverlay
            type="errored"
            title={localDisplayTitle}
            media={file}
            duration={duration}
            isCompact={isCompact}
            retryButtonProps={{
              onRetry: async () => {
                if (file.libraryId) {
                  trackingStore.mediaRetried({ mediaId: file._id, libraryId: file.libraryId });
                  // Remove from selection when retry starts
                  videoSelection.deselectItems([file._id]);
                  const result = await folder.retryMedia(file.libraryId, file._id);
                  if ('error' in result) {
                    toast.custom(
                      (t) => (
                        <Toast
                          id={t}
                          header="Media retry failed"
                          description={result.message}
                          variant="error"
                        />
                      ),
                      {
                        duration: 10000,
                      },
                    );
                  } else {
                    folder.refreshMediaById(file._id);
                  }
                }
              },
              tooltip: 'Retry processing',
            }}
            selectionCheckboxProps={{
              selection: videoSelection,
              itemId: file._id,
            }}
            closeButtonProps={{
              onClose: () => {
                // No need for confirmation modal when errored.
                folder.deleteMedia(file._id);
              },
              tooltip: 'Remove errored media',
            }}
          />
        ),
        previewContent: thumbnailUrl ? (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ) : (
          <MediaThumbnail
            thumbnailUrl={null}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            showIfNoThumbnail={true}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ),
      },
      // IMPORTANT: 'empty' is a valid transitional state (media entity created but not yet processing),
      // NOT an error state. It occurs briefly after upload completes while waiting for backend to start processing.
      // We show processing UI here to avoid confusing error flashes during normal upload flow.
      // TODO: Refactor status config architecture to make transitional states more explicit and readable.
      // Consider separating "genuine error states" from "valid transitional states" at the type level.
      // NOTE: This config is effectively dead code for grid view - LibraryContentMediaList.tsx:178 filters out
      // all 'empty' status files. Empty files only show via virtual upload cards (Path A above). This config
      // may be used in other contexts like table view or detail views.
      empty: {
        statusOverlay: (
          <StatusOverlay
            type="processing"
            subtype="processing-without-video"
            progress={0}
            title={localDisplayTitle}
            media={file}
            duration={duration}
            isCompact={isCompact}
            selectionCheckboxProps={{
              selection: videoSelection,
              itemId: file._id,
            }}
            closeButtonProps={
              canDelete
                ? {
                    onClose: () => {
                      folder.deleteMedia(file._id);
                    },
                    tooltip: 'Delete media',
                  }
                : undefined
            }
          />
        ),
        previewContent: thumbnailUrl ? (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ) : (
          <MediaThumbnail
            thumbnailUrl={null}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            showIfNoThumbnail={true}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ),
      },
      processing: getProcessingStatusConfig(
        file,
        thumbnailUrl,
        thumbnailToken,
        renderVideo,
        folder,
        openDeleteModal,
        canDelete,
        isSelected,
        displayProgress,
        duration,
        localDisplayTitle,
        persistedLocalThumbnail,
        isCompact,
        isAudio,
      ),
      uploading: {
        statusOverlay: (
          <StatusOverlay
            type="uploading"
            progress={progress ?? 0}
            timeLeft=""
            title={localDisplayTitle}
            media={file}
            duration={duration}
            isCompact={isCompact}
            closeButtonProps={
              canDelete
                ? {
                    onClose: createDeleteHandler(file),
                    tooltip: 'Delete uploading media',
                  }
                : undefined
            }
            selectionCheckboxProps={
              isSelected
                ? {
                    selection: videoSelection,
                    itemId: file._id,
                  }
                : undefined
            }
          />
        ),
        previewContent: thumbnailUrl ? (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ) : (
          <MediaThumbnail
            thumbnailUrl={null}
            alt={getMediaAltText(file)}
            gradientSeed={file.uniqueId || file._id}
            showIfNoThumbnail={true}
            token={thumbnailToken}
            localThumbnail={persistedLocalThumbnail}
            isAudio={isAudio}
          />
        ),
      },
      completed: {
        statusOverlay: null,
        previewContent: (
          <VideoCard
            media={file}
            src={src || ''}
            libraryId={file.libraryId}
            mediaId={file._id}
            thumbnail={thumbnailUrl || undefined}
            thumbnailToken={thumbnailToken}
            isAudio={isAudio}
            cellWidth={cellWidth}
          />
        ),
      },
    };

    // A failed/cancelled client-side upload leaves the backend media orphaned in
    // a pre-processing state ('empty'/'uploading') — the server still reports it
    // as uploading. Honor the client-side error so the card shows the failed
    // state instead of a forever-"Uploading (0%)".
    // NOTE: in-session only — uploadStatus is in-memory, so after a reload the
    // orphaned 'uploading' media reads "Uploading" again until backend
    // reconciliation deletes it (tracked as a follow-up).
    if (isFailedOrphanUpload(file, activeUploadStatus)) {
      return {
        ...configs.errored,
        statusOverlay: (
          <StatusOverlay
            type="errored"
            title={localDisplayTitle}
            media={file}
            duration={duration}
            isCompact={isCompact}
            // No "Retry processing" here: this media never finished uploading, so
            // folder.retryMedia (a processing retry) can't apply — offer removal only.
            selectionCheckboxProps={{ selection: videoSelection, itemId: file._id }}
            closeButtonProps={{
              onClose: () => {
                // Clear the row locally right away (works offline); the backend
                // delete of the orphaned media is deferred + retried when online.
                folder.removeMediaLocally(file._id);
                if (file.libraryId) {
                  uploadManager.enqueueMediaDeletion(file.libraryId, file._id);
                }
              },
              tooltip: 'Remove failed upload',
            }}
          />
        ),
      };
    }

    return (
      configs[status] || {
        statusOverlay: null,
        previewContent: <span>{status}</span>,
      }
    );
  };

  const { statusOverlay, previewContent } = getStatusConfig();

  if (status === 'completed') {
    return previewContent;
  }

  const handleClick = () => {
    // Handle selection mode
    if (videoSelection.isSelecting) {
      if (status === 'errored' || status === 'empty') {
        videoSelection.toggleVideoSelection(file._id);
        trackingStore.mediaSelectionChanged({ totalSelected: videoSelection.selectedCount });
      }
      return;
    }

    // Handle opening modal for processing media
    if (canOpenModal) {
      navigate(
        getMediaUrl(file.libraryId, file._id, {
          libraryName: libraryManager.libraryById(file.libraryId)?.name,
          mediaName: file.metadata?.name,
        }),
      );
    }
  };

  /*
   For errored/empty status, selection is fully supported (visual + click to toggle).
   For other non-completed statuses (queued, uploading, processing), the card will
   visually show as selected if it's in the selection, but clicking won't toggle.
   For processing status, clicking opens the modal if canOpenModal is true.
   This allows errored cards to remain visually selected after retry when status changes.
   */
  // VideoCard (used when processing-with-video) has its own internal gradient overlays.
  // Adding them again here would cover VideoCard's title.
  const hasVideoPreview = status === 'processing' && !!file.capabilities?.preview;

  return (
    <SelectableCard
      isSelected={isSelected}
      onClick={handleClick}
      className={hasVideoPreview ? css.card : css.root}
      // The wrapped VideoCard draws the edge, so it carries the hover too.
      liftOnHover={!hasVideoPreview}
      style={{ cursor: canOpenModal ? 'pointer' : 'default' }}
      {...divProps}
    >
      {previewContent}
      {!hasVideoPreview && <GradientOverlay zIndex={2} borderRadius={14} />}
      {!hasVideoPreview && (
        <GradientOverlay
          variant="bottom-blur"
          backdropBlur={20}
          height={20}
          zIndex={3}
          borderRadius={14}
        />
      )}
      {statusOverlay}
    </SelectableCard>
  );
});

export const MediaCardFileUpload = observer(function MediaCardFileUpload({
  localFileInfo,
}: {
  localFileInfo: FileUploadStatus;
}) {
  const progress = Math.max(0, Math.min(100, localFileInfo.progress || 0));
  const { subtext } = calculateUploadStats(localFileInfo, progress);
  const background = (
    <Thumbnail
      alt={localFileInfo.file.name}
      gradientSeed={localFileInfo.fingerprint}
      localThumbnail={localFileInfo.localThumbnail}
    />
  );

  // Show "Queued" when file is waiting in upload queue
  if (localFileInfo.status === 'queued') {
    return (
      <SelectableCard className={css.root} isSelected={false}>
        {background}
        <StatusOverlay type="queued-upload" />
      </SelectableCard>
    );
  }

  return (
    <SelectableCard className={css.root} isSelected={false}>
      {background}
      <StatusOverlay
        type="uploading"
        progress={Math.floor(localFileInfo.progress)}
        timeLeft=""
        subtext={subtext}
      />
    </SelectableCard>
  );
});
